import React, { useEffect } from 'react'
import { MenuItem, Button, Divider } from '@mui/material'
import AccountCircleMenu from '../../../Assets/Images/svgs/account-circle-menu'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import styles from './ProfileMenu.module.scss'
import { BuildOutlined, SettingsOutlined } from '@mui/icons-material'
import { MUISwitch } from '../../Shared'
import { getLocalStorage } from '../../../Helpers/Storage'
import { MAINTENANCE_ROLE_ID } from '../../../Constants/Texts'
import { useAppDispatch, useAppSelector } from '../../../Store/Hooks'
import {
  fetchMaintenanceStatus,
  updateMaintenanceStatus,
  selectMaintenanceMode,
} from '../../../Store/Slice/system-settings.slice'

interface ProfileMenuProps {
  userName: string
  userEmail: string
  onLogout: () => void
  onClose: () => void
  onListingPref: () => void
  onNotificationPref: () => void
  onMaintenanceToggle?: (checked: boolean) => void
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({
  userName,
  userEmail,
  onLogout,
  onClose,
  onListingPref,
  onNotificationPref,
  onMaintenanceToggle,
}) => {
  const dispatch = useAppDispatch()
  const userData: any = getLocalStorage('user_data')
  const isMaintenanceRole = userData?.roleId === MAINTENANCE_ROLE_ID
  const maintenanceMode = useAppSelector(selectMaintenanceMode)

  useEffect(() => {
    if (isMaintenanceRole && maintenanceMode === null) {
      dispatch(fetchMaintenanceStatus() as any)
    }
  }, [isMaintenanceRole, maintenanceMode, dispatch])

  const handleMaintenanceToggle = (event: any) => {
    const checked = event?.target?.checked
    dispatch(updateMaintenanceStatus(checked) as any)
    if (onMaintenanceToggle) {
      onMaintenanceToggle(checked)
    }
  }

  return (
    <div className={styles.profileMenuContainer}>
      <div className={styles.profileHeader}>
        <AccountCircleMenu />
        <div className={styles.userInfo}>
          <div className={styles.userName}>{userName}</div>
          <div className={styles.userEmail}>{userEmail}</div>
        </div>
      </div>
      <Divider className={styles.divider} />
      <div className={styles.listData}>
        <MenuItem
          component='a'
          href='/listing-screen-preference'
          className={styles.menuItem}
          onClick={(e) => {
            if (!(e.ctrlKey || e.metaKey || e.button === 1)) {
              e.preventDefault()
              onListingPref()
            }
          }}
        >
          <div className={styles.iconContainer}>
            <SettingsOutlined className={styles.menuIcon} />
          </div>
          <div className={styles.menuTextContainer}>
            <div className={styles.menuTitle}>Listing Screen</div>
            <div className={styles.menuSubtitle}>View settings</div>
          </div>
        </MenuItem>
        <MenuItem
          component='a'
          href='/notifications-preference'
          className={styles.menuItem}
          onClick={(e) => {
            if (!(e.ctrlKey || e.metaKey || e.button === 1)) {
              e.preventDefault()
              onNotificationPref()
            }
          }}
        >
          <div className={styles.iconContainer}>
            <NotificationsNoneIcon className={styles.menuIcon} />
          </div>
          <div className={styles.menuTextContainer}>
            <div className={styles.menuTitle}>Notifications</div>
            <div className={styles.menuSubtitle}>Manage alerts</div>
          </div>
        </MenuItem>
        {isMaintenanceRole && (
          <MenuItem className={styles.menuItem}>
            <div className={styles.iconContainer}>
              <BuildOutlined className={styles.menuIcon} style={{ transform: 'rotate(90deg)' }} />
            </div>
            <div className={styles.menuTextContainer}>
              <div className={styles.menuTitle}>Maintenance Mode</div>
              <div className={styles.menuSubtitle}>Site maintenance</div>
            </div>
            <MUISwitch
              name='maintenance-mode-switch'
              checked={maintenanceMode || false}
              onChange={(evt: any) => handleMaintenanceToggle(evt)}
            />
          </MenuItem>
        )}
      </div>

      <div className={styles.buttonRow}>
        <Button variant='contained' color='error' className={styles.logoutBtn} onClick={onLogout}>
          Logout
        </Button>
        <Button variant='outlined' className={styles.closeBtn} onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  )
}

export default ProfileMenu
